import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_SUBSCRIPTION_STATUS } from "../../graphql/queries";
import { summarizeSubscription } from "../../utils/subscription";
import { getReportById as fetchReportById } from "../../utils/api/reportApi";
import ReportViewerContent from "../ReportsPage/ReportViewerContent";
import { getReportTitle } from "../ReportsPage/reportViewUtils";
import styles from "./ReportPrintPage.module.css";

const BENCHMARK_VIEW_CONFIG = {
  "brand-assets": { label: "Brand assets" },
  "tone-and-voice": { label: "Tone & voice" },
};

const ReportPrintPage = () => {
  const { reportId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthUser();
  const { t } = useLocalization();
  const { shopData } = useShop();
  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;
  const headers = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
  const prefetchedReport = location.state?.prefetchedReport || null;
  const prefetchedReportId = String(
    prefetchedReport?._id || prefetchedReport?.id || "",
  ).trim();
  const isPrefetchedMatch =
    Boolean(prefetchedReportId) &&
    prefetchedReportId === String(reportId || "").trim();

  const [report, setReport] = useState(() => {
    return isPrefetchedMatch ? prefetchedReport : null;
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [activeGscTab] = useState("indexing");
  const [activeMetaTab] = useState("performance");
  const [activeRevenueTab] = useState("revenue_reality");

  const { data: subData } = useQuery(GET_SUBSCRIPTION_STATUS, {
    skip: !authToken,
    fetchPolicy: "cache-and-network",
    context: headers,
  });

  const subSummary = useMemo(
    () => summarizeSubscription(subData?.getSubscriptionStatus),
    [subData],
  );

  useEffect(() => {
    let active = true;
    if (!reportId || !userId || !authToken) return undefined;
    if (isPrefetchedMatch) {
      return undefined;
    }

    const loadReport = async () => {
      setReportLoading(true);
      setReportError(null);
      try {
        const response = await fetchReportById({ reportId, userId, authToken });
        if (!active) return;
        setReport(response?.report || null);
      } catch (error) {
        if (!active) return;
        setReportError(error);
        setReport(null);
      } finally {
        if (active) {
          setReportLoading(false);
        }
      }
    };

    loadReport();

    return () => {
      active = false;
    };
  }, [reportId, userId, authToken, isPrefetchedMatch]);
  const resolvedReport = isPrefetchedMatch ? prefetchedReport : report;

  const focusedViewRaw = String(searchParams.get("view") || "").trim();
  const focusedBenchmarkView =
    resolvedReport?.data?.collection_name === "shop_benchmark_report" &&
    Object.prototype.hasOwnProperty.call(BENCHMARK_VIEW_CONFIG, focusedViewRaw)
      ? focusedViewRaw
      : null;

  const reportTitle = getReportTitle(resolvedReport, t);
  const title = focusedBenchmarkView
    ? BENCHMARK_VIEW_CONFIG[focusedBenchmarkView].label
    : reportTitle;
  const subtitle = focusedBenchmarkView ? reportTitle : shopData?.name || "";

  const ready = !reportLoading && !reportError && Boolean(resolvedReport?.data);

  return (
    <main className={styles.page} data-pdf-ready={ready ? "true" : "false"}>
      <section className={styles.sheet}>
        <header className={styles.header}>
          <div>
            <p className={styles.kicker}>Zeqi Report</p>
            <h1 className={styles.title}>{title || "Report"}</h1>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
        </header>

        {reportLoading ? (
          <div className={styles.stateBlock}>Loading report…</div>
        ) : reportError ? (
          <div className={styles.stateBlock}>
            {reportError?.message || "Unable to load report for export."}
          </div>
        ) : resolvedReport?.data ? (
          <div className={styles.viewerWrap}>
            <ReportViewerContent
              report={resolvedReport}
              focusedView={focusedBenchmarkView}
              activeTab={activeGscTab}
              activeMetaTab={activeMetaTab}
              activeRevenueTab={activeRevenueTab}
              isMonthlySubscription={subSummary?.isMonthly}
            />
          </div>
        ) : (
          <div className={styles.stateBlock}>No report data available.</div>
        )}
      </section>
    </main>
  );
};

export default ReportPrintPage;
